import { AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { reportError } from '../lib/sentry';

/**
 * `errorElement` for the router: catches render/loader/action errors anywhere in the
 * routed tree so a single bad page can't blank the whole app. `RootErrorBoundary` below
 * covers the (much rarer) case of an error thrown outside the router entirely.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!notFound) reportError(error);
  }, [error, notFound]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <AlertTriangle size={40} className="text-danger-fg" aria-hidden />
      <div>
        <p className="text-lg font-bold">{notFound ? t('common.notFoundTitle') : t('common.errorTitle')}</p>
        <p className="mt-1 text-sm text-text-muted">{notFound ? t('common.notFoundHint') : t('common.errorHint')}</p>
      </div>
      <div className="flex gap-2">
        <button className="btn-secondary" onClick={() => navigate(-1)}>
          {t('common.goBack')}
        </button>
        <button className="btn-primary" onClick={() => (notFound ? navigate('/') : window.location.reload())}>
          {notFound ? t('common.goHome') : t('common.reload')}
        </button>
      </div>
    </div>
  );
}
